import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";
import { Chips } from "primereact/chips";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import { Divider } from "primereact/divider";
import {
  apiService,
  PhishingDomainCandidate,
  PhishingDomainCurrent,
  PhishingOngoingJob,
  Project,
  ProjectTenant,
} from "../../services/apiService";
import { useToast } from "../../contexts/ToastContext";

type SelectedDomainDraft = {
  domain: string;
  subdomains: string[];
};

const POLL_MS = 5000;
const POLL_MAX_MS = 10 * 60 * 1000;

function isJobActivelyRunning(status: string | undefined): boolean {
  const s = (status || "").toLowerCase();
  return s !== "" && !["completed", "failed", "canceled", "terminated"].includes(s);
}

function statusSeverity(
  status: string,
): "success" | "info" | "warning" | "danger" | "secondary" | undefined {
  switch (status) {
    case "registered":
      return "success";
    case "pending":
    case "deregistering":
      return "warning";
    case "failed":
      return "danger";
    default:
      return "secondary";
  }
}

const Phishing: React.FC = () => {
  const toast = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectReference, setProjectReference] = useState<string | null>(null);
  const [tenants, setTenants] = useState<ProjectTenant[]>([]);
  const [phishingTenant, setPhishingTenant] = useState<ProjectTenant | null>(
    null,
  );
  const [relayTenant, setRelayTenant] = useState<ProjectTenant | null>(null);
  const [candidates, setCandidates] = useState<PhishingDomainCandidate[]>([]);
  const [current, setCurrent] = useState<PhishingDomainCurrent[]>([]);
  const [ongoing, setOngoing] = useState<PhishingOngoingJob[]>([]);
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [subdomainsByDomain, setSubdomainsByDomain] = useState<
    Record<string, string[]>
  >({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reservingTenant, setReservingTenant] = useState(false);
  const [refreshingJobs, setRefreshingJobs] = useState(false);
  const pollStartedAt = useRef<number | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    (async () => {
      const res = await apiService.getProjects();
      if (res.success) setProjects(res.data || []);
      else toast.showError("Failed", res.message || "Unable to load projects");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    pollStartedAt.current = null;
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const projectOptions = projects.map((p) => ({
    label: `${p.reference || ""} — ${p.name}`,
    value: p.reference || "",
  }));

  const applyDomainPayload = (
    candidatesList: PhishingDomainCandidate[],
    currentList: PhishingDomainCurrent[],
    ongoingList: PhishingOngoingJob[],
  ) => {
    setCandidates(candidatesList);
    setCurrent(currentList);
    setOngoing(ongoingList);
    const active = currentList.filter(
      (row) =>
        row.phishing_status === "registered" ||
        row.phishing_status === "pending" ||
        row.phishing_status === "failed" ||
        row.phishing_status === "deregistering",
    );
    setSelectedDomains(active.map((row) => row.domainname));
    const subs: Record<string, string[]> = {};
    active.forEach((row) => {
      subs[row.domainname] = row.subdomains || [];
    });
    setSubdomainsByDomain(subs);
  };

  const loadProject = async (reference: string) => {
    setLoading(true);
    setSelectedDomains([]);
    setSubdomainsByDomain({});
    setOngoing([]);

    const [tenantRes, domainRes] = await Promise.all([
      apiService.getProjectTenants(reference),
      apiService.getPhishingDomains(reference),
    ]);

    if (tenantRes.success) {
      const list = tenantRes.data || [];
      setTenants(list);
      setPhishingTenant(list.find((t) => t.phishing_tenant) || null);
      setRelayTenant(list.find((t) => t.relay_tenant) || null);
    } else {
      toast.showError("Tenants", tenantRes.message || "Unable to load tenants");
    }

    if (domainRes.success) {
      applyDomainPayload(
        domainRes.data.candidates || [],
        domainRes.data.current || [],
        domainRes.data.ongoing || [],
      );
    } else {
      toast.showError("Domains", domainRes.message || "Unable to load domains");
    }

    setLoading(false);
  };

  const refreshJobs = useCallback(
    async (reference: string, opts?: { quiet?: boolean }) => {
      if (!opts?.quiet) setRefreshingJobs(true);
      const res = await apiService.refreshPhishingJobs(reference);
      if (!opts?.quiet) setRefreshingJobs(false);

      if (!res.success) {
        if (!opts?.quiet) {
          toast.showError("Jobs", res.message || "Unable to refresh jobs");
        }
        return;
      }

      const creds = res.data.credentials || [];
      creds.forEach((c) => {
        toast.showSuccess(
          "Credentials ready",
          `${c.fqdn}: ${c.user_upn} / ${c.user_password}`,
        );
      });

      setOngoing(res.data.ongoing || []);

      const domainRes = await apiService.getPhishingDomains(reference);
      if (domainRes.success) {
        applyDomainPayload(
          domainRes.data.candidates || [],
          domainRes.data.current || [],
          domainRes.data.ongoing || res.data.ongoing || [],
        );
      }

      if ((res.data.ongoing || []).every((j) => !isJobActivelyRunning(j.runtime_status))) {
        stopPolling();
      }
    },
    [stopPolling, toast],
  );

  const startPolling = useCallback(
    (reference: string) => {
      stopPolling();
      pollStartedAt.current = Date.now();
      pollTimer.current = setInterval(() => {
        if (
          pollStartedAt.current &&
          Date.now() - pollStartedAt.current > POLL_MAX_MS
        ) {
          stopPolling();
          toast.showWarn(
            "Still running",
            "Phishing registration is taking longer than expected. Use Refresh status to check again.",
          );
          return;
        }
        void refreshJobs(reference, { quiet: true });
      }, POLL_MS);
    },
    [refreshJobs, stopPolling, toast],
  );

  useEffect(() => {
    const active = ongoing.some((j) => isJobActivelyRunning(j.runtime_status));
    if (projectReference && active && !pollTimer.current) {
      startPolling(projectReference);
    }
    if (!active) {
      stopPolling();
    }
  }, [ongoing, projectReference, startPolling, stopPolling]);

  const reservePhishingTenant = async () => {
    if (!projectReference) return;
    setReservingTenant(true);
    const ensure = await apiService.ensurePhishingTenant(projectReference);
    setReservingTenant(false);
    if (!ensure.success) {
      toast.showError("Phishing tenant", ensure.message || "Unable to assign");
      return;
    }
    if (ensure.data.assigned) {
      toast.showSuccess("Phishing tenant", "Assigned available phishing tenant");
    } else {
      toast.showInfo("Phishing tenant", ensure.data.message || "Already assigned");
    }
    await loadProject(projectReference);
  };

  const candidateOptions = useMemo(
    () =>
      candidates.map((c) => ({
        label: `${c.domainname} (${c.source})`,
        value: c.domainname,
      })),
    [candidates],
  );

  const submit = async () => {
    if (!projectReference) return;
    if (!phishingTenant) {
      toast.showWarn("Phishing tenant", "Reserve a phishing tenant first");
      return;
    }
    if (selectedDomains.length === 0) {
      toast.showWarn("Validation", "Select at least one domain");
      return;
    }
    const payload: SelectedDomainDraft[] = selectedDomains.map((domain) => ({
      domain,
      subdomains: subdomainsByDomain[domain] || [],
    }));
    setSubmitting(true);
    const res = await apiService.submitPhishingDomains(projectReference, {
      domains: payload,
      comment: "phishing registration",
    });
    setSubmitting(false);
    if (res.success) {
      toast.showSuccess("Started", res.data.message || "Registration started");
      await loadProject(projectReference);
      startPolling(projectReference);
    } else {
      toast.showError("Error", res.message || "Submit failed");
    }
  };

  const deregister = async (domain: string) => {
    if (!projectReference) return;
    const res = await apiService.deregisterPhishingDomains(projectReference, [
      domain,
    ]);
    if (res.success) {
      toast.showSuccess("Deregister started", domain);
      await loadProject(projectReference);
      startPolling(projectReference);
    } else {
      toast.showError("Error", res.message || "Deregister failed");
    }
  };

  const terminateJob = async (instanceId: string) => {
    if (!projectReference) return;
    const res = await apiService.terminatePhishingJob(projectReference, instanceId);
    if (res.success) {
      toast.showSuccess("Terminated", res.data.message || instanceId);
      await refreshJobs(projectReference);
    } else {
      toast.showError("Terminate failed", res.message || "Unable to terminate");
    }
  };

  const restartJob = async (instanceId: string) => {
    if (!projectReference) return;
    const res = await apiService.restartPhishingJob(projectReference, instanceId);
    if (res.success) {
      toast.showSuccess("Restarted", res.data.message || instanceId);
      await refreshJobs(projectReference);
      startPolling(projectReference);
    } else {
      toast.showError("Restart failed", res.message || "Unable to restart");
    }
  };

  return (
    <div className="flex flex-column gap-3">
      <div className="flex flex-wrap gap-2 align-items-center justify-content-between">
        <h2 className="m-0 text-xl">Phishing</h2>
        <Dropdown
          value={projectReference}
          options={projectOptions}
          onChange={(e) => {
            stopPolling();
            setProjectReference(e.value);
            if (e.value) loadProject(e.value);
          }}
          placeholder="Select project"
          filter
          className="w-20rem"
        />
      </div>

      {!projectReference && (
        <Message
          severity="info"
          text="Select a project to manage phishing domains."
        />
      )}

      {projectReference && (
        <>
          <div className="grid">
            <div className="col-12 md:col-6">
              <div className="surface-100 p-3 border-round">
                <div className="text-600 mb-1">Phishing tenant</div>
                {phishingTenant ? (
                  <div className="font-medium">
                    {phishingTenant.tenant_provider ||
                      phishingTenant.tenant_tf_name ||
                      phishingTenant.tenant_id}{" "}
                    ({phishingTenant.tenant_name})
                  </div>
                ) : (
                  <div className="flex flex-column gap-2">
                    <div className="font-medium">
                      {loading ? "Loading…" : "Not assigned"}
                    </div>
                    {!loading && (
                      <Button
                        label="Reserve phishing tenant"
                        icon="pi pi-plus"
                        className="p-button-sm w-max"
                        loading={reservingTenant}
                        onClick={reservePhishingTenant}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="surface-100 p-3 border-round">
                <div className="text-600 mb-1">Relay tenant</div>
                <div className="font-medium">
                  {relayTenant
                    ? `${relayTenant.tenant_provider || relayTenant.tenant_tf_name || relayTenant.tenant_id} (${relayTenant.tenant_name})`
                    : "None (optional)"}
                </div>
              </div>
            </div>
          </div>

          {!phishingTenant && !loading && (
            <Message
              severity="warn"
              text="Reserve a phishing tenant before selecting domains."
            />
          )}

          <Divider />
          <h3 className="m-0">Register / update domains</h3>
          <p className="text-600 m-0">
            Choose available domains or domains already reserved for this project
            (e.g. relay). Optional subdomains become separate FQDNs. Submit
            reserves the domain and starts Azure registration jobs (can take a
            while).
          </p>

          <label className="font-medium">Domains</label>
          <MultiSelect
            value={selectedDomains}
            options={candidateOptions}
            onChange={(e) => setSelectedDomains(e.value || [])}
            filter
            placeholder="Select one or more domains"
            className="w-full"
            display="chip"
            disabled={loading || !phishingTenant}
          />

          {selectedDomains.map((domain) => (
            <div key={domain} className="surface-100 p-3 border-round">
              <div className="font-medium mb-2">{domain}</div>
              <label className="block mb-1 text-600">Subdomains</label>
              <Chips
                value={subdomainsByDomain[domain] || []}
                onChange={(e) =>
                  setSubdomainsByDomain((prev) => ({
                    ...prev,
                    [domain]: e.value || [],
                  }))
                }
                separator=","
                className="w-full"
                placeholder="e.g. login, portal"
                disabled={!phishingTenant}
              />
            </div>
          ))}

          <div className="flex justify-content-end gap-2">
            <Button
              icon="pi pi-refresh"
              className="p-button-outlined"
              loading={loading}
              onClick={() => projectReference && loadProject(projectReference)}
            />
            <Button
              label="Submit registration"
              icon="pi pi-check"
              loading={submitting}
              disabled={!phishingTenant || selectedDomains.length === 0}
              onClick={submit}
            />
          </div>

          <Divider />
          <div className="flex flex-wrap align-items-center justify-content-between gap-2">
            <h3 className="m-0">Current phishing reservations</h3>
            <Button
              label="Refresh status"
              icon="pi pi-sync"
              className="p-button-outlined p-button-sm"
              loading={refreshingJobs}
              disabled={!phishingTenant && ongoing.length === 0}
              onClick={() =>
                projectReference && refreshJobs(projectReference)
              }
            />
          </div>
          <DataTable
            value={current}
            loading={loading}
            emptyMessage="No phishing domain registrations yet"
            size="small"
          >
            <Column field="domainname" header="Domain" />
            <Column
              header="Status"
              body={(row: PhishingDomainCurrent) => (
                <Tag
                  value={row.phishing_status || "—"}
                  severity={statusSeverity(row.phishing_status)}
                />
              )}
            />
            <Column
              header="Subdomains"
              body={(row: PhishingDomainCurrent) =>
                (row.subdomains || []).join(", ") || "—"
              }
            />
            <Column
              header="Also relay"
              body={(row: PhishingDomainCurrent) =>
                row.relay_tenant_id ? "yes" : "no"
              }
            />
            <Column
              header=""
              body={(row: PhishingDomainCurrent) =>
                row.phishing_status === "registered" ||
                row.phishing_status === "pending" ||
                row.phishing_status === "failed" ? (
                  <Button
                    label="Deregister"
                    className="p-button-sm p-button-outlined p-button-danger"
                    onClick={() => deregister(row.domainname)}
                  />
                ) : null
              }
            />
          </DataTable>

          <Divider />
          <h3 className="m-0">Ongoing phishing registration</h3>
          <p className="text-600 m-0">
            Azure Durable Function jobs for register/deregister. Status is polled
            automatically while jobs are running. You can terminate or restart a
            job from here.
          </p>
          <DataTable
            value={ongoing}
            loading={refreshingJobs}
            emptyMessage="No ongoing jobs"
            size="small"
          >
            <Column field="domainname" header="Domain" />
            <Column field="fqdn" header="FQDN" />
            <Column field="action" header="Action" />
            <Column
              header="Azure status"
              body={(row: PhishingOngoingJob) => (
                <Tag
                  value={row.runtime_status || "—"}
                  severity={
                    row.error
                      ? "danger"
                      : row.runtime_status?.toLowerCase() === "completed"
                        ? "success"
                        : row.runtime_status?.toLowerCase() === "failed" ||
                            row.runtime_status?.toLowerCase() === "terminated"
                          ? "danger"
                          : "warning"
                  }
                />
              )}
            />
            <Column
              header="Updated"
              body={(row: PhishingOngoingJob) =>
                row.last_updated_time || row.updated_at || "—"
              }
            />
            <Column
              header="Error"
              body={(row: PhishingOngoingJob) => row.error || "—"}
            />
            <Column
              header=""
              body={(row: PhishingOngoingJob) => (
                <div className="flex gap-2">
                  {row.can_terminate ? (
                    <Button
                      label="Terminate"
                      className="p-button-sm p-button-outlined p-button-danger"
                      onClick={() => terminateJob(row.instance_id)}
                    />
                  ) : null}
                  {row.can_restart ? (
                    <Button
                      label="Restart"
                      className="p-button-sm p-button-outlined"
                      onClick={() => restartJob(row.instance_id)}
                    />
                  ) : null}
                </div>
              )}
            />
          </DataTable>

          {tenants.length === 0 && !loading && (
            <Message
              severity="warn"
              text="No tenants reserved for this project yet."
            />
          )}
        </>
      )}
    </div>
  );
};

export default Phishing;
